import { createAsyncThunk, createSlice } from '@reduxjs/toolkit'
import { toastHelper } from '../util/toastHelper.js'
const REQUEST_URL = `${process.env.REACT_APP_REQUEST_URL}/api`
const API_VERSION = 'v1'

const headers = {
	Accept: 'application/json',
	'Content-Type': 'application/json',
}
export const getAllProposals = createAsyncThunk(
	'getAllProposals',
	async (payload = {}) => {
		try {
			const queryString = new URLSearchParams(
				Object.entries(payload).filter(([_, value]) => value != null)
			).toString()

			const url = `${REQUEST_URL}/${API_VERSION}/proposal/get-proposals${queryString ? `?${queryString}` : ''}`
			const res = await fetch(url, {
				method: 'GET',
				credentials: 'include',
				headers,
			})
			const result = await res.json()

			return result
		} catch (err) {
			console.log(err.message)
		}
	}
)

export const getKazooProposals = createAsyncThunk(
	'getKazooProposals',
	async (kazooId) => {
		try {
			const res = await fetch(
				`${REQUEST_URL}/${API_VERSION}/proposal/kazoo-proposals/${kazooId}`,
				{
					method: 'GET',
					credentials: 'include',
					headers,
				}
			)
			const result = await res.json()
			return result
		} catch (err) {
			console.log(err.message)
		}
	}
)

export const requestProposal = createAsyncThunk(
	'requestProposal',
	async (data) => {
		try {
			const res = await fetch(
				`${REQUEST_URL}/${API_VERSION}/proposal/request-proposal`,
				{
					method: 'POST',
					credentials: 'include',
					headers,
					body: JSON.stringify(data),
				}
			)
			const result = await res.json()
			return result
		} catch (err) {
			console.log(err.message)
		}
	}
)

export const makeBid = createAsyncThunk('makeBid', async (data) => {
	try {
		const res = await fetch(
			`${REQUEST_URL}/${API_VERSION}/proposal/make-bid/${data.proposalId}`,
			{
				method: 'PATCH',
				credentials: 'include',
				headers,
				body: JSON.stringify(data),
			}
		)
		const result = await res.json()
		return result
	} catch (err) {
		console.log(err.message)
	}
})

export const updateProposal = createAsyncThunk(
	'updateProposal',
	async (data) => {
		try {
			const res = await fetch(
				`${REQUEST_URL}/${API_VERSION}/proposal/update-proposal/${data.proposalId}`,
				{
					method: 'PATCH',
					credentials: 'include',
					headers,
					body: JSON.stringify(data),
				}
			)
			const result = await res.json()
			return result
		} catch (err) {
			console.log(err.message)
		}
	}
)

export const requestBidRevision = createAsyncThunk(
	'requestBidRevision',
	async (data) => {
		try {
			const res = await fetch(
				`${REQUEST_URL}/${API_VERSION}/proposal/request-bid-revision/${data.proposalId}`,
				{
					method: 'POST',
					credentials: 'include',
					headers,
					body: JSON.stringify(data),
				}
			)
			const result = await res.json()
			return result
		} catch (err) {
			console.log(err.message)
		}
	}
)

export const updateProposalRequest = createAsyncThunk(
	'updateProposalRequest',
	async (data) => {
		try {
			const res = await fetch(
				`${REQUEST_URL}/${API_VERSION}/proposal/update-proposal-request/${data.proposalId}`,
				{
					method: 'PUT',
					credentials: 'include',
					headers,
					body: JSON.stringify(data),
				}
			)
			const result = await res.json()
			return result
		} catch (err) {
			console.log(err.message)
		}
	}
)

export const updateReviewProposal = createAsyncThunk(
	'updateReviewProposal',
	async ({ proposalId, formData }) => {
		try {
			const res = await fetch(
				`${REQUEST_URL}/${API_VERSION}/proposal/update-review-proposal/${proposalId}`,
				{
					method: 'POST',
					credentials: 'include',
					body: formData,
				}
			)
			return res.json()
		} catch (err) {
			console.log(err.message)
			throw err
		}
	}
)
export const sendReminder = createAsyncThunk('sendReminder', async (data) => {
	try {
		const res = await fetch(
			`${REQUEST_URL}/${API_VERSION}/proposal/send-sendReminder`,
			{
				method: 'POST',
				credentials: 'include',
				headers,
				body: JSON.stringify(data),
			}
		)
		const result = await res.json()
		return result
	} catch (err) {
		console.log(err.message)
	}
})

export const getProposalHistory = createAsyncThunk(
	'getProposalHistory',
	async (proposalId) => {
		try {
			const res = await fetch(
				`${REQUEST_URL}/${API_VERSION}/proposal/get-proposal-history/${proposalId}`,
				{
					method: 'GET',
					credentials: 'include',
					headers,
				}
			)
			const result = await res.json()
			return result
		} catch (err) {
			console.log(err.message)
		}
	}
)

const initialState = {
	proposals: [],
	singleProposal: {},
	proposalHistory: [],
}

const proposalSlice = createSlice({
	name: 'proposal',
	initialState,
	reducers: {},
	extraReducers: (builder) => {
		builder
			.addCase(getAllProposals.fulfilled, (state, { payload }) => {
				if (!payload || !payload.success) {
					toastHelper(
						payload?.message || 'Failed to fetch proposals',
						'error'
					)
					return
				}
				state.proposals = payload.proposals
			})
			.addCase(requestProposal.fulfilled, (state, { payload }) => {
				if (!payload || !payload.success) {
					toastHelper(
						payload?.message || 'Failed to request proposal',
						'error'
					)
					return
				}
				toastHelper(payload?.message || 'Proposal requested', 'success')
			})
			.addCase(makeBid.fulfilled, (state, { payload }) => {
				if (!payload || !payload.success) {
					toastHelper(
						payload?.message || 'Failed to make bid',
						'error'
					)
					return
				}
				toastHelper(
					payload?.message || 'Bid made successfully',
					'success'
				)
				state.proposals = state.proposals.filter(
					(proposal) => proposal.proposalId !== payload.proposalId
				)
			})
			.addCase(getKazooProposals.fulfilled, (state, { payload }) => {
				if (!payload || !payload.success) {
					toastHelper(
						payload?.message || 'Failed to fetch proposals',
						'error'
					)
					return
				}
				state.proposals = payload.proposals
			})
			.addCase(updateProposal.fulfilled, (state, { payload }) => {
				if (!payload || !payload.success) {
					toastHelper(
						payload?.message || 'Failed to update proposal',
						'error'
					)
					return
				}
				toastHelper(
					payload?.message || 'Proposal updated successfully',
					'success'
				)
			})
			.addCase(requestBidRevision.fulfilled, (state, { payload }) => {
				if (!payload || !payload.success) {
					toastHelper(
						payload?.message || 'Failed to request bid revision',
						'error'
					)
					return
				}
				toastHelper(
					payload?.message || 'Bid revision requested successfully',
					'success'
				)
			})
			.addCase(updateProposalRequest.fulfilled, (state, { payload }) => {
				if (!payload || !payload.success) {
					toastHelper(
						payload?.message || 'Failed to update proposal',
						'error'
					)
					return
				}
				toastHelper(
					payload?.message || 'Proposal updated successfully',
					'success'
				)
			})
			.addCase(updateReviewProposal.fulfilled, (state, { payload }) => {
				if (!payload || !payload.success) {
					toastHelper(
						payload?.message || 'Failed to submit review',
						'error'
					)
					return
				}
				toastHelper(
					payload?.message || 'Review submitted successfully',
					'success'
				)
			})
			.addCase(updateReviewProposal.rejected, (state, action) => {
				toastHelper(
					action.error?.message || 'Failed to submit review',
					'error'
				)
			})
			.addCase(getProposalHistory.fulfilled, (state, { payload }) => {
				if (payload && payload.success)
					state.proposalHistory = payload.proposalData
			})
	},
})

export default proposalSlice.reducer
